// Manager for the icon generation Web Worker
// Handles communication with the worker and provides a Promise-based API

class IconWorkerManager {
  constructor() {
    this.worker = null;
    this.pendingRequests = new Map();
    this.requestId = 0;
    this.isInitialized = false;
  }

  async initialize() {
    if (this.isInitialized) return;

    try {
      this.worker = new Worker('/iconWorker.js?v=' + Date.now());

      this.worker.onmessage = (e) => {
        const { id, type, result } = e.data;

        if (type === 'iconGenerated') {
          const request = this.pendingRequests.get(id);
          if (request) {
            this.pendingRequests.delete(id);
            if (result.success) {
              request.resolve(result.url);
            } else {
              request.resolve(result.fallbackUrl);
            }
          }
        }
      };

      this.worker.onerror = (error) => {
        console.error('Icon worker error:', error);
        // Reject all pending requests
        this.pendingRequests.forEach(request => {
          request.reject(new Error('Worker error'));
        });
        this.pendingRequests.clear();
      };

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize icon worker:', error);
      throw error;
    }
  }

  async generateCircleIcon(imageSrc, pvSum, borderColorOverride = null, paddingScale = 1, displaySize = 90) {
    if (!this.isInitialized) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const id = ++this.requestId;

      this.pendingRequests.set(id, { resolve, reject });

      this.worker.postMessage({
        id,
        type: 'generateIcon',
        data: {
          imageSrc,
          pvSum,
          borderColorOverride,
          paddingScale,
          displaySize
        }
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        if (this.pendingRequests.has(id)) {
          this.pendingRequests.delete(id);
          reject(new Error('Icon generation timeout'));
        }
      }, 10000);
    });
  }

  terminate() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
      this.isInitialized = false;
    }
    // Reject all pending requests
    this.pendingRequests.forEach(request => {
      request.reject(new Error('Worker terminated'));
    });
    this.pendingRequests.clear();
  }
}

// Export singleton instance
export const iconWorkerManager = new IconWorkerManager();